import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ConfirmationOptions {
  title: string | ReactNode;
  message: string;
  confirmText?: string;
  confirmTestId?: string;
  cancelTestId?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
}

interface SaveConfirmationOptions {
  title: string | ReactNode;
  message: string;
  saveText?: string;
  discardText?: string;
  cancelText?: string;
}

type SaveConfirmationResult = "save" | "discard" | "cancel";

interface ConfirmationState extends ConfirmationOptions {
  isOpen: boolean;
  resolve?: (value: boolean) => void;
  variant: "default" | "destructive";
}

interface SaveConfirmationState extends SaveConfirmationOptions {
  isOpen: boolean;
  resolve?: (value: SaveConfirmationResult) => void;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
  confirmSave: (
    options: SaveConfirmationOptions,
  ) => Promise<SaveConfirmationResult>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(
  undefined,
);

interface ConfirmationProviderProps {
  children: ReactNode;
}

export function ConfirmationProvider({ children }: ConfirmationProviderProps) {
  // Standard confirmation dialog state
  const [state, setState] = useState<ConfirmationState>({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    variant: "default",
  });

  // Save confirmation dialog state (3 options: save, discard, cancel)
  const [saveState, setSaveState] = useState<SaveConfirmationState>({
    isOpen: false,
    title: "",
    message: "",
    saveText: "Save Changes",
    discardText: "Discard Changes",
    cancelText: "Cancel",
  });

  const confirm = useCallback(
    (options: ConfirmationOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        setState({
          isOpen: true,
          title: options.title,
          message: options.message,
          confirmText: options.confirmText || "Confirm",
          confirmTestId: options.confirmTestId,
          cancelTestId: options.cancelTestId,
          cancelText: options.cancelText || "Cancel",
          variant: options.variant || "default",
          resolve,
        });
      });
    },
    [],
  );

  const confirmSave = useCallback(
    (options: SaveConfirmationOptions): Promise<SaveConfirmationResult> => {
      return new Promise((resolve) => {
        setSaveState({
          isOpen: true,
          title: options.title,
          message: options.message,
          saveText: options.saveText || "Save Changes",
          discardText: options.discardText || "Discard Changes",
          cancelText: options.cancelText || "Cancel",
          resolve,
        });
      });
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    state.resolve?.(true);
    setState((prev) => ({ ...prev, isOpen: false, resolve: undefined }));
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    state.resolve?.(false);
    setState((prev) => ({ ...prev, isOpen: false, resolve: undefined }));
  }, [state.resolve]);

  const handleSave = useCallback(() => {
    saveState.resolve?.("save");
    setSaveState((prev) => ({ ...prev, isOpen: false, resolve: undefined }));
  }, [saveState.resolve]);

  const handleDiscard = useCallback(() => {
    saveState.resolve?.("discard");
    setSaveState((prev) => ({ ...prev, isOpen: false, resolve: undefined }));
  }, [saveState.resolve]);

  const handleSaveCancel = useCallback(() => {
    saveState.resolve?.("cancel");
    setSaveState((prev) => ({ ...prev, isOpen: false, resolve: undefined }));
  }, [saveState.resolve]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  const handleSaveOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleSaveCancel();
      }
    },
    [handleSaveCancel],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      // Let focused buttons keep native Enter activation, especially Cancel.
      if (event.target instanceof Element && event.target.closest("button"))
        return;
      if (event.key === "Enter" && !event.defaultPrevented) {
        event.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm],
  );

  const handleSaveKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" && !event.defaultPrevented) {
        event.preventDefault();
        handleSave();
      }
    },
    [handleSave],
  );

  return (
    <ConfirmationContext.Provider value={{ confirm, confirmSave }}>
      {children}

      {/* Standard confirmation dialog */}
      <AlertDialog open={state.isOpen} onOpenChange={handleOpenChange}>
        <AlertDialogContent
          className="sm:max-w-[420px] p-0 gap-0"
          onKeyDown={handleKeyDown}
        >
          <AlertDialogHeader className="px-6 pt-6 pb-4 border-b">
            <AlertDialogTitle className="text-white">
              {state.title}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="px-6 py-5">
            <AlertDialogDescription
              className="text-sm"
              style={{
                color: "rgba(var(--color-primitive-neutral-0-rgb),0.7)",
              }}
            >
              {state.message}
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter className="px-6 pt-4 pb-6 border-t gap-3">
            <button
              type="button"
              onClick={handleCancel}
              data-testid={state.cancelTestId}
              className="inline-flex items-center justify-center font-medium select-none active:scale-95"
              style={{
                height: "32px",
                padding: "0 14px",
                borderRadius: "var(--radius-default)",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.15s ease",
                background: "transparent",
                border: "none",
                color: "var(--color-interactive-utility-text)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "rgba(var(--color-primitive-neutral-0-rgb),0.08)";
                e.currentTarget.style.color =
                  "var(--color-interactive-utility-texthover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color =
                  "var(--color-interactive-utility-text)";
              }}
            >
              {state.cancelText}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              data-testid={state.confirmTestId}
              autoFocus
              className="inline-flex items-center justify-center font-medium select-none active:scale-[0.97]"
              style={{
                height: "32px",
                padding: "0 14px",
                borderRadius: "var(--radius-default)",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.15s ease",
                cursor: "pointer",
                ...(state.variant === "destructive"
                  ? {
                      background: "var(--color-primitive-pink-500)",
                      border: "1px solid var(--color-primitive-pink-500)",
                      color: "var(--color-primitive-neutral-0)",
                    }
                  : {
                      background: "var(--color-primitive-blue-500)",
                      border: "1px solid var(--color-primitive-blue-500)",
                      color: "var(--color-primitive-neutral-0)",
                    }),
              }}
              onMouseEnter={(e) => {
                if (state.variant === "destructive") {
                  e.currentTarget.style.background =
                    "var(--color-interactive-pink-bghover)";
                  e.currentTarget.style.borderColor =
                    "var(--color-interactive-pink-bghover)";
                } else {
                  e.currentTarget.style.background =
                    "var(--color-interactive-cobalt-bghover)";
                  e.currentTarget.style.borderColor =
                    "var(--color-interactive-cobalt-bghover)";
                }
              }}
              onMouseLeave={(e) => {
                if (state.variant === "destructive") {
                  e.currentTarget.style.background =
                    "var(--color-primitive-pink-500)";
                  e.currentTarget.style.borderColor =
                    "var(--color-primitive-pink-500)";
                } else {
                  e.currentTarget.style.background =
                    "var(--color-primitive-blue-500)";
                  e.currentTarget.style.borderColor =
                    "var(--color-primitive-blue-500)";
                }
              }}
            >
              {state.confirmText}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save confirmation dialog (3 options) */}
      <AlertDialog open={saveState.isOpen} onOpenChange={handleSaveOpenChange}>
        <AlertDialogContent
          className="sm:max-w-[500px] p-0 gap-0"
          onKeyDown={handleSaveKeyDown}
        >
          <AlertDialogHeader className="px-6 pt-6 pb-4 border-b">
            <AlertDialogTitle className="text-white">
              {saveState.title}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="px-6 py-5">
            <AlertDialogDescription
              className="text-sm"
              style={{
                color: "rgba(var(--color-primitive-neutral-0-rgb),0.7)",
              }}
            >
              {saveState.message}
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter className="px-6 pt-4 pb-6 border-t flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleSaveCancel}
              className="inline-flex items-center justify-center font-medium whitespace-nowrap"
              style={{
                height: "32px",
                padding: "0 14px",
                borderRadius: "var(--radius-default)",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.15s ease",
                background: "rgba(var(--color-primitive-neutral-0-rgb),0.05)",
                border:
                  "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.12)",
                color: "var(--color-primitive-neutral-200)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "rgba(var(--color-primitive-neutral-0-rgb),0.1)";
                e.currentTarget.style.borderColor =
                  "rgba(var(--color-primitive-neutral-0-rgb),0.2)";
                e.currentTarget.style.color =
                  "var(--color-primitive-neutral-0)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "rgba(var(--color-primitive-neutral-0-rgb),0.05)";
                e.currentTarget.style.borderColor =
                  "rgba(var(--color-primitive-neutral-0-rgb),0.12)";
                e.currentTarget.style.color =
                  "var(--color-primitive-neutral-200)";
              }}
            >
              {saveState.cancelText}
            </button>
            <button
              type="button"
              onClick={handleDiscard}
              className="inline-flex items-center justify-center font-medium whitespace-nowrap"
              style={{
                height: "32px",
                padding: "0 14px",
                borderRadius: "var(--radius-default)",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.15s ease",
                background: "rgba(var(--color-primitive-red-500-rgb),0.15)",
                border:
                  "1px solid rgba(var(--color-primitive-red-500-rgb),0.4)",
                color: "var(--color-primitive-red-500)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "rgba(var(--color-primitive-red-500-rgb),0.25)";
                e.currentTarget.style.borderColor =
                  "rgba(var(--color-primitive-red-500-rgb),0.6)";
                e.currentTarget.style.color =
                  "var(--color-interactive-red-texthover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "rgba(var(--color-primitive-red-500-rgb),0.15)";
                e.currentTarget.style.borderColor =
                  "rgba(var(--color-primitive-red-500-rgb),0.4)";
                e.currentTarget.style.color = "var(--color-primitive-red-500)";
              }}
            >
              {saveState.discardText}
            </button>
            <button
              type="button"
              onClick={handleSave}
              autoFocus
              className="inline-flex items-center justify-center font-medium whitespace-nowrap select-none active:scale-[0.97]"
              style={{
                height: "32px",
                padding: "0 14px",
                borderRadius: "var(--radius-default)",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.15s ease",
                background: "var(--color-primitive-blue-500)",
                border: "1px solid var(--color-primitive-blue-500)",
                color: "var(--color-primitive-neutral-0)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "var(--color-interactive-cobalt-bghover)";
                e.currentTarget.style.borderColor =
                  "var(--color-interactive-cobalt-bghover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "var(--color-primitive-blue-500)";
                e.currentTarget.style.borderColor =
                  "var(--color-primitive-blue-500)";
              }}
            >
              {saveState.saveText}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmationContext.Provider>
  );
}

export function useConfirmationDialog() {
  const context = useContext(ConfirmationContext);
  if (context === undefined) {
    throw new Error(
      "useConfirmationDialog must be used within a ConfirmationProvider",
    );
  }
  return context;
}
